import React, { useEffect, useMemo, useState } from "react";
import {
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  setRolePermissions,
  getPermissionCatalog,
  getPermissionPresets,
  syncPermissionCatalog,
} from "../services/api";
import { usePermissions } from "../context/PermissionContext";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import PageLoader from "../components/PageLoader";
import "./PurchaseOrders.css";
import "./RoleManagement.css";

interface RoleRow {
  roleId: number;
  name: string;
  description?: string;
  isSystem: boolean;
  displayOrder: number;
  permissionCount: number;
  userCount: number;
}
interface PresetItem { code: string; name: string; description: string; autoGrant: boolean; permissionIds: number[] }
interface CatalogItem {
  permissionId: number;
  code: string;
  screenCode: string;
  screenName: string;
  tabCode: string | null;
  tabName: string | null;
  action: string;
  category: string;
  displayOrder: number;
}

const ACTION_LABELS: Record<string, string> = {
  view: "Görüntüle",
  create: "Ekle",
  update: "Güncelle",
  delete: "Sil",
  export: "Dışa Aktar",
  transfer: "Transfer",
  send: "Fatura Oluştur",
  manage: "İptal / İtiraz",
  approve: "Onayla / Reddet",
  post: "Cariye İşle",
};
const ACTION_ORDER = ["view", "create", "update", "delete", "export", "transfer", "send", "manage", "approve", "post"];

const I = {
  search: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>,
  sync: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36" /><path d="M21 3v6h-6" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3Z" /><path d="m9 12 2 2 4-4" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.5a3.5 3.5 0 0 1 0 7M18 14.5a6.5 6.5 0 0 1 3.5 5.5" /></svg>,
  inbox: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="M4 13h5l2 3h2l2-3h5" /></svg>,
};

const NO_CONFIRM = { isOpen: false, title: "", message: "", confirmText: "", type: "danger" as "warning" | "danger", onConfirm: () => {} };

const RoleManagement: React.FC = () => {
  const { refresh: refreshPerms } = usePermissions();
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedPerms, setSelectedPerms] = useState<Set<number>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [showCodes, setShowCodes] = useState(false);

  const [roleModal, setRoleModal] = useState<{
    open: boolean;
    id: number | null;
    name: string;
    description: string;
    isSystem: boolean;
  }>({ open: false, id: null, name: "", description: "", isSystem: false });

  const [alert, setAlert] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    type: "warning" | "danger";
    onConfirm: () => void;
  }>(NO_CONFIRM);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [rolesRes, catRes, preRes] = await Promise.all([getRoles(), getPermissionCatalog(), getPermissionPresets().catch(() => ({ data: [] }))]);
      const rolesData: RoleRow[] = rolesRes.data || [];
      setRoles(rolesData);
      setCatalog(catRes.data || []);
      setPresets((preRes.data || []).filter((p: PresetItem) => p.permissionIds.length > 0));
      if (rolesData.length > 0) await selectRole(selectedId ?? rolesData[0].roleId);
    } catch {
      setAlert({ isOpen: true, message: "Roller/izin kataloğu yüklenemedi.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const selectRole = async (id: number) => {
    try {
      const res = await getRole(id);
      setSelectedId(id);
      setSelectedPerms(new Set<number>(res.data?.permissionIds || []));
      setDirty(false);
    } catch {
      setAlert({ isOpen: true, message: "Rol izinleri yüklenemedi.", type: "error" });
    }
  };

  /** Kaydedilmemiş değişiklik varken başka role geçmeden önce sorar. */
  const requestSelect = (id: number) => {
    if (id === selectedId) return;
    if (!dirty) { selectRole(id); return; }
    setConfirm({
      isOpen: true,
      title: "Değişiklikler kaydedilmedi",
      message: "Bu roldeki yetki değişiklikleri kaydedilmedi. Başka role geçerseniz değişiklikler kaybolur.",
      confirmText: "Kaydetmeden geç",
      type: "warning",
      onConfirm: () => { setConfirm(NO_CONFIRM); selectRole(id); },
    });
  };

  const discard = () => { if (selectedId != null) selectRole(selectedId); };

  const selectedRole = roles.find((r) => r.roleId === selectedId) || null;

  // Katalog → kategori ▸ ekran ▸ item[]
  const grouped = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    const filtered = q
      ? catalog.filter(
          (c) =>
            c.screenName.toLocaleLowerCase("tr").includes(q) ||
            (c.tabName || "").toLocaleLowerCase("tr").includes(q) ||
            c.category.toLocaleLowerCase("tr").includes(q) ||
            c.code.includes(q),
        )
      : catalog;
    const byCat = new Map<string, Map<string, CatalogItem[]>>();
    for (const it of [...filtered].sort((a, b) => a.displayOrder - b.displayOrder)) {
      if (!byCat.has(it.category)) byCat.set(it.category, new Map());
      const screens = byCat.get(it.category)!;
      if (!screens.has(it.screenCode)) screens.set(it.screenCode, []);
      screens.get(it.screenCode)!.push(it);
    }
    return byCat;
  }, [catalog, search]);

  const setMany = (ids: number[], value: boolean) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (value ? next.add(id) : next.delete(id)));
      return next;
    });
    setDirty(true);
  };
  const toggle = (id: number) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setDirty(true);
  };

  const savePermissions = async () => {
    if (selectedId == null) return;
    try {
      setSaving(true);
      await setRolePermissions(selectedId, Array.from(selectedPerms));
      setAlert({ isOpen: true, message: "Rol yetkileri kaydedildi.", type: "success" });
      setDirty(false);
      await refreshPerms();
      setRoles((await getRoles()).data || []);
    } catch {
      setAlert({ isOpen: true, message: "Yetkiler kaydedilemedi.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    try {
      setSaving(true);
      const res = await syncPermissionCatalog();
      setAlert({
        isOpen: true,
        message: `Katalog senkronlandı. ${res.data?.added ?? 0} yeni yetki eklendi.${res.data?.presetsSeeded ? ` İK self-servis paketi ${res.data.presetsSeeded} role verildi (${(res.data.seededRoles || []).join(", ")}).` : ""}`,
        type: "success",
      });
      await loadAll();
    } catch {
      setAlert({ isOpen: true, message: "Katalog senkronlanamadı.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () =>
    setRoleModal({ open: true, id: null, name: "", description: "", isSystem: false });
  const openEdit = (r: RoleRow) =>
    setRoleModal({ open: true, id: r.roleId, name: r.name, description: r.description || "", isSystem: r.isSystem });
  const closeRoleModal = () => setRoleModal((m) => ({ ...m, open: false }));

  const saveRole = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving) return;
    if (!roleModal.name.trim() && !roleModal.isSystem) {
      setAlert({ isOpen: true, message: "Rol adı zorunludur.", type: "warning" });
      return;
    }
    try {
      setSaving(true);
      if (roleModal.id == null) {
        const res = await createRole({ name: roleModal.name.trim(), description: roleModal.description });
        setRoleModal({ ...roleModal, open: false });
        setRoles((await getRoles()).data || []);
        if (res.data?.roleId) await selectRole(res.data.roleId);
      } else {
        await updateRole(roleModal.id, { name: roleModal.name.trim(), description: roleModal.description });
        setRoleModal({ ...roleModal, open: false });
        setRoles((await getRoles()).data || []);
      }
    } catch (e: any) {
      setAlert({ isOpen: true, message: "Rol kaydedilemedi: " + (e.response?.data || e.message), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (r: RoleRow) =>
    setConfirm({
      isOpen: true,
      title: "Rolü sil",
      message: `"${r.name}" rolünü silmek istediğinize emin misiniz?`,
      confirmText: "Evet, sil",
      type: "danger",
      onConfirm: async () => {
        try {
          await deleteRole(r.roleId);
          setConfirm(NO_CONFIRM);
          if (selectedId === r.roleId) setSelectedId(null);
          setRoles((await getRoles()).data || []);
        } catch (e: any) {
          setConfirm(NO_CONFIRM);
          setAlert({ isOpen: true, message: "Rol silinemedi: " + (e.response?.data || e.message), type: "error" });
        }
      },
    });

  // Rol modalı: Esc kapatır (capture; alttaki dinleyicilere ulaşmaz)
  useEffect(() => {
    if (!roleModal.open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) { e.stopPropagation(); setRoleModal((m) => ({ ...m, open: false })); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [roleModal.open, saving]);

  const allCatalogIds = catalog.map((c) => c.permissionId);
  const allOn = allCatalogIds.length > 0 && allCatalogIds.every((id) => selectedPerms.has(id));
  const totalUsers = roles.reduce((s, r) => s + (r.userCount || 0), 0);

  if (loading && roles.length === 0) return <PageLoader message="Roller yükleniyor..." />;

  return (
    <div className={`po-page rm ${dirty ? "rm-has-bar" : ""}`}>
      <header className="po-head">
        <div>
          <span className="po-kicker">Yönetim</span>
          <h1>Rol yönetimi</h1>
          <p>Rolleri oluşturun; her rolün hangi ekran, sekme ve işlemi kullanabileceğini seçin.</p>
        </div>
        <div className="po-head-actions">
          <button type="button" className="po-btn" onClick={handleSync} disabled={saving} title="İzin kataloğunu güncelle">
            <span className={saving && !roleModal.open ? "po-spin-icon" : ""} style={{ display: "inline-flex" }}>{I.sync}</span> Kataloğu senkronla
          </button>
          <button type="button" className="po-btn primary" onClick={openCreate}>{I.plus} Yeni rol</button>
        </div>
      </header>

      <section className="po-kpis">
        <div className="po-kpi navy"><span className="l">Rol</span><span className="v">{roles.length}</span><span className="s"><b>{roles.filter((r) => r.isSystem).length}</b> yerleşik · <b>{roles.filter((r) => !r.isSystem).length}</b> özel</span></div>
        <div className="po-kpi"><span className="l">Yetki kataloğu</span><span className="v">{catalog.length}<small> yetki</small></span><span className="s"><b>{new Set(catalog.map((c) => c.screenCode)).size}</b> ekran · <b>{new Set(catalog.map((c) => c.category)).size}</b> grup</span></div>
        <div className="po-kpi"><span className="l">Rol atanmış kullanıcı</span><span className="v">{totalUsers}</span><span className="s">tüm rollerin toplamı</span></div>
      </section>

      <div className={`rm-layout ${loading ? "rm-dim" : ""}`}>
        {/* Rol listesi */}
        <nav className="po-card rm-roles" aria-label="Roller">
          <div className="rm-roles-head">Roller <i>{roles.length}</i></div>
          <ul>
            {roles.map((r) => (
              <li key={r.roleId}>
                <button type="button" className={`rm-role ${selectedId === r.roleId ? "on" : ""}`} aria-current={selectedId === r.roleId ? "true" : undefined} onClick={() => requestSelect(r.roleId)}>
                  <span className="rm-role-name">{r.name}{r.isSystem && <span className="rm-sys" title="Yerleşik rol">Yerleşik</span>}</span>
                  <span className="rm-role-meta">{r.permissionCount} yetki · {r.userCount} kullanıcı</span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="rm-role-new" onClick={openCreate}>{I.plus} Yeni rol</button>
        </nav>

        {/* Yetki matrisi */}
        {selectedRole ? (
          <section className="po-card rm-panel">
            <div className="rm-panel-head">
              <div className="rm-panel-title">
                <span className="rm-panel-icon">{I.shield}</span>
                <div>
                  <h2>
                    {selectedRole.name}
                    {selectedRole.isSystem && <span className="po-badge info">Yerleşik</span>}
                    {dirty && <span className="po-dirty">kaydedilmedi</span>}
                  </h2>
                  <p>
                    {selectedRole.description || "Ekran ve işlem yetkilerini seçin"}
                    <span className="rm-dot">·</span><b>{selectedPerms.size}</b> yetki
                    <span className="rm-dot">·</span>{I.users}<b>{selectedRole.userCount}</b> kullanıcı
                    <span className="rm-dot">·</span>claim <code className="po-codepill rm-claim" title="Bu rolün yetki (claim) adı">{selectedRole.name}</code>
                  </p>
                </div>
              </div>
              <div className="rm-panel-actions">
                <button type="button" className="po-btn small" onClick={() => openEdit(selectedRole)}>{I.edit} Düzenle</button>
                {!selectedRole.isSystem && (
                  <button type="button" className="po-btn small danger" onClick={() => askDelete(selectedRole)}>{I.trash} Sil</button>
                )}
              </div>
            </div>

            {/* Hazır paketler — tek tıkla demet ver / kaldır */}
            {presets.length > 0 && (
              <div className="rm-presets">
                <span className="rm-presets-label">Hazır paketler</span>
                {presets.map((p) => {
                  const on = p.permissionIds.every((id) => selectedPerms.has(id));
                  const some = !on && p.permissionIds.some((id) => selectedPerms.has(id));
                  return (
                    <button key={p.code} type="button" className={`rm-preset ${on ? "on" : some ? "some" : ""}`} title={p.description} aria-pressed={on} onClick={() => setMany(p.permissionIds, !on)}>
                      <span className="rm-preset-box" aria-hidden="true">{on ? "✓" : some ? "–" : ""}</span>
                      {p.name}
                      {p.autoGrant && <em title="Katalog eşitlemesinde İK izni olmayan her role kendiliğinden verilir">otomatik</em>}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="po-toolbar">
              <div className="po-search">
                {I.search}
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ekran, sekme, grup veya yetki kodu ara" aria-label="Yetki ara" />
                {search && <button type="button" className="po-ib" aria-label="Temizle" onClick={() => setSearch("")}>{I.close}</button>}
              </div>
              <label className="po-check rm-codes">
                <input type="checkbox" checked={showCodes} onChange={(e) => setShowCodes(e.target.checked)} />
                Yetki kodlarını göster
              </label>
              <button type="button" className="po-btn small" onClick={() => setMany(allCatalogIds, !allOn)} disabled={allCatalogIds.length === 0}>
                {allOn ? "Tümünü temizle" : "Tümünü seç"}
              </button>
            </div>

            {catalog.length === 0 ? (
              <div className="po-blank"><span className="po-blank-icon">{I.inbox}</span><b>İzin kataloğu boş</b>Yetkileri listelemek için kataloğu senkronlayın.
                <button type="button" className="po-btn primary" onClick={handleSync} disabled={saving}>{I.sync} Kataloğu senkronla</button></div>
            ) : grouped.size === 0 ? (
              <div className="po-blank"><span className="po-blank-icon">{I.search}</span><b>Aramayla eşleşen yetki yok</b>Başka bir ekran ya da kod deneyin.
                <button type="button" className="po-btn" onClick={() => setSearch("")}>Aramayı temizle</button></div>
            ) : (
              <div className="rm-matrix">
                {Array.from(grouped.entries()).map(([category, screens]) => {
                  // kategorideki mevcut aksiyon sütunları
                  const present = new Set<string>();
                  screens.forEach((items) => items.forEach((i) => present.add(i.action)));
                  const cols = ACTION_ORDER.filter((a) => present.has(a));
                  const catIds = Array.from(screens.values()).flat().map((i) => i.permissionId);
                  const catAllOn = catIds.every((id) => selectedPerms.has(id));
                  const catOnCount = catIds.filter((id) => selectedPerms.has(id)).length;

                  return (
                    <div key={category} className="rm-cat">
                      <div className="rm-cat-bar">
                        <h3>{category}<em>{catOnCount}/{catIds.length}</em></h3>
                        <button type="button" className="po-link" onClick={() => setMany(catIds, !catAllOn)}>
                          {catAllOn ? "Grubu temizle" : "Grubun tümü"}
                        </button>
                      </div>
                      <div className="po-table-wrap tight">
                        <table className="po-table small rm-table">
                          <thead>
                            <tr>
                              <th className="rm-th-screen">Ekran / sekme</th>
                              {cols.map((a) => {
                                const colIds = Array.from(screens.values())
                                  .flat()
                                  .filter((i) => i.action === a)
                                  .map((i) => i.permissionId);
                                const colOn = colIds.length > 0 && colIds.every((id) => selectedPerms.has(id));
                                return (
                                  <th key={a} className="rm-th-act">
                                    <button type="button" className="rm-col-toggle" title={colOn ? "Sütunu temizle" : "Sütunun tümünü seç"} onClick={() => setMany(colIds, !colOn)}>
                                      {ACTION_LABELS[a] || a}
                                    </button>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {Array.from(screens.entries()).map(([screenCode, items]) => {
                              const screenName = items[0].screenName;
                              const hasTabs = items.some((i) => i.tabCode);
                              const rows: { label: string; code: string; sub: boolean; items: CatalogItem[] }[] = [];
                              if (hasTabs) {
                                rows.push({ label: screenName, code: screenCode, sub: false, items: [] });
                                const tabs = new Map<string, CatalogItem[]>();
                                for (const it of items) {
                                  const k = it.tabCode || "_";
                                  if (!tabs.has(k)) tabs.set(k, []);
                                  tabs.get(k)!.push(it);
                                }
                                tabs.forEach((tabItems, k) =>
                                  rows.push({
                                    label: tabItems[0].tabName || k,
                                    code: `${screenCode}:${k}`,
                                    sub: true,
                                    items: tabItems,
                                  }),
                                );
                              } else {
                                rows.push({ label: screenName, code: screenCode, sub: false, items });
                              }

                              return rows.map((row, ri) => {
                                const rowIds = row.items.map((i) => i.permissionId);
                                // Sekmeli ekranın grup başlık satırı (kendi işlemi yok)
                                if (row.items.length === 0) {
                                  return (
                                    <tr key={screenCode + ri} className="static rm-tr-screen">
                                      <td className="rm-td-screen" colSpan={cols.length + 1}>
                                        <span className="rm-row-label">{row.label}</span>
                                        {showCodes && <code className="rm-row-code">{row.code}</code>}
                                      </td>
                                    </tr>
                                  );
                                }
                                const rowAllOn = rowIds.length > 0 && rowIds.every((id) => selectedPerms.has(id));
                                return (
                                  <tr key={screenCode + ri} className={`static ${row.sub ? "rm-tr-sub" : ""}`}>
                                    <td className="rm-td-screen">
                                      <div className="rm-screen">
                                        <span className="rm-row-label">{row.label}</span>
                                        {showCodes && <code className="rm-row-code">{row.code}</code>}
                                        <button type="button" className="rm-row-toggle" onClick={() => setMany(rowIds, !rowAllOn)} title="Satırın tümünü seç/temizle">
                                          {rowAllOn ? "Temizle" : "Tümü"}
                                        </button>
                                      </div>
                                    </td>
                                    {cols.map((a) => {
                                      const it = row.items.find((i) => i.action === a);
                                      if (!it) return <td key={a} className="rm-td-na"><span aria-hidden="true">—</span></td>;
                                      const on = selectedPerms.has(it.permissionId);
                                      return (
                                        <td key={a} className="rm-td-act">
                                          <label className="rm-cell" title={it.code}>
                                            <input type="checkbox" checked={on} onChange={() => toggle(it.permissionId)} aria-label={`${row.label}: ${ACTION_LABELS[a] || a}`} />
                                          </label>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              });
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        ) : (
          <section className="po-card rm-panel">
            <div className="po-blank"><span className="po-blank-icon">{I.shield}</span><b>{roles.length ? "Bir rol seçin" : "Henüz rol yok"}</b>
              {roles.length ? "Yetkilerini görmek için soldaki listeden bir rol seçin." : "İlk rolü oluşturup yetkilerini seçin."}
              {!roles.length && <button type="button" className="po-btn primary" onClick={openCreate}>{I.plus} Yeni rol</button>}
            </div>
          </section>
        )}
      </div>

      {/* Yapışkan kaydet çubuğu */}
      <div className={`rm-bar ${dirty ? "show" : ""}`} aria-hidden={!dirty}>
        <div className="rm-bar-inner">
          <div className="rm-bar-text"><b>Kaydedilmemiş değişiklikler var</b>{selectedRole ? `${selectedRole.name} rolünde ${selectedPerms.size} yetki seçili. ` : ""}Kaydedene kadar eski yetkiler geçerli.</div>
          <div className="rm-bar-btns">
            <button type="button" className="po-btn" onClick={discard} disabled={saving || !dirty} tabIndex={dirty ? 0 : -1}>Vazgeç</button>
            <button type="button" className="po-btn primary" onClick={savePermissions} disabled={!dirty || saving} tabIndex={dirty ? 0 : -1}>
              {saving ? "Kaydediliyor..." : "Yetkileri kaydet"}
            </button>
          </div>
        </div>
      </div>

      {/* Rol modalı */}
      {roleModal.open && (
        <div className="po-overlay center top" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) closeRoleModal(); }}>
          <form className="po-modal" role="dialog" aria-modal="true" aria-labelledby="rm-modal-title" onSubmit={saveRole}>
            <div className="po-modal-head">
              <div><span className="po-kicker">Rol</span><h3 id="rm-modal-title">{roleModal.id == null ? "Yeni rol" : "Rolü düzenle"}</h3></div>
              <button type="button" className="po-ib big" aria-label="Kapat" onClick={closeRoleModal} disabled={saving}>{I.close}</button>
            </div>
            <div className="po-modal-body">
              <label className="po-field">
                <span>Rol adı (claim){roleModal.isSystem ? <em>yerleşik, değiştirilemez</em> : <em>zorunlu</em>}</span>
                <input
                  type="text"
                  value={roleModal.name}
                  disabled={roleModal.isSystem}
                  autoFocus={!roleModal.isSystem}
                  maxLength={100}
                  onChange={(e) => setRoleModal({ ...roleModal, name: e.target.value })}
                  placeholder="Örn: Depo Sorumlusu"
                />
                <span className="po-fieldnote">Bu ad aynı zamanda rolün yetki (claim) adıdır.</span>
              </label>
              <label className="po-field">
                <span>Açıklama</span>
                <textarea rows={3} value={roleModal.description} autoFocus={roleModal.isSystem} onChange={(e) => setRoleModal({ ...roleModal, description: e.target.value })} />
              </label>
            </div>
            <div className="po-modal-foot">
              <button type="button" className="po-btn" onClick={closeRoleModal} disabled={saving}>Vazgeç</button>
              <button type="submit" className="po-btn primary" disabled={saving}>{saving ? "Kaydediliyor..." : roleModal.id == null ? "Rolü oluştur" : "Kaydet"}</button>
            </div>
          </form>
        </div>
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, isOpen: false })} />
      <ConfirmModal
        isOpen={confirm.isOpen}
        title={confirm.title}
        message={confirm.message}
        type={confirm.type}
        confirmText={confirm.confirmText}
        cancelText="Vazgeç"
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(NO_CONFIRM)}
      />
    </div>
  );
};

export default RoleManagement;
